import PropTypes from 'prop-types';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { getDeviceType } from '@xanadu/ui-shared/utils/deviceDetection';

import IconEye from '../../../../../assets/images/icons/misc/icon-eye-show.svg';
import IconEyeHidden from '../../../../../assets/images/icons/misc/icon-eye-hidden.svg';

import styles from './InputField.module.scss';

export default function InputField({
  id,
  className = '',
  type,
  dataHook,
  name,
  label,
  subLabel = '',
  theme,
  onPropertyChange,
  onInputBlur,
  onInputFocus,
  focusOnMount,
  fullWidth,
  value,
  error = '',
  disabled,
  maxLength,
  placeholder,
  inline,
  info,
  isMobile,
  showInputError = true,
  size = 'normal',
  errorAlign = 'right',
  autoComplete = 'off',
}) {
  const [pwdVisibility, setPwdVisibility] = useState(false);
  const field = useRef();
  const inputElement = useRef();

  useEffect(() => {
    let focusTimeout = null;
    if (focusOnMount) {
      if (getDeviceType() === 'desktop') {
        inputElement.current.focus();
      } else {
        focusTimeout = setTimeout(() => {
          inputElement.current.focus();
          focusTimeout = null;
          clearTimeout(focusTimeout);
        }, 450);
      }
    }
  }, []);

  const onInputChange = useCallback((e) => {
    onPropertyChange({
      name,
      value: e.target.value,
    });
  });

  const onInputFocusFunction = useCallback(() => {
    if (inputElement.current) {
      inputElement.current.classList.add(styles['input--focused']);
    }
    if (typeof onInputFocus === 'function') {
      onInputFocus();
    }
  });

  const onInputBlurFunction = useCallback((e) => {
    if (typeof onInputBlur === 'function') {
      onInputBlur({
        name,
        value: e.target.value,
      });
    }
    if (inputElement.current) {
      inputElement.current.classList.remove(styles['input--focused']);
    }
  });

  const handleShowHidePassword = useCallback(() => {
    setPwdVisibility(!pwdVisibility);
  });

  let pwdHandler = null;
  let errorClass;
  let iconEye = (<IconEyeHidden />);
  let inputError = (
    <div className={`${styles.containerMessages} ${isMobile && styles['containerMessages--mobile']}`}>
      <span className={styles.errorMessage} data-hook={`${dataHook}-error`}>
        {error}
      </span>
    </div>
  );

  if (error.length > 0) {
    errorClass = styles['input--error'];
  } else {
    errorClass = '';
    inputError = null;
  }

  if (pwdVisibility) {
    iconEye = (<IconEye />);
  }

  let passwordClass = '';
  if (name.toLowerCase().includes('password')) {
    passwordClass = styles['input--password'];
    pwdHandler = (
      <div
        role="button"
        className={`${styles.sHiP} ${styles.sHiPIcon}`}
        onClick={() => handleShowHidePassword()}
      >
        {iconEye}
      </div>
    );
  }

  const inputFieldClass = [
    styles.inputField,
    className,
    styles[`inputField--${size}`],
    styles[`inputField--errorAlign--${errorAlign}`],
    inline ? styles['inputField--inline'] : '',
    error ? styles['inputField--withError'] : '',
    fullWidth ? styles['inputField--fullWidth'] : '',
    styles[`inputField--${theme}`]
  ].join(' ');

  return (
    <div className={inputFieldClass}>
      <div
        className={styles.property}
        ref={field}
      >
        <label htmlFor={name} className={styles.label}>
          {label}
          <span className={styles.subLabel}>{subLabel}</span>
        </label>
        <div className={styles.inputContainer}>
          <input
            id={name}
            data-hook={dataHook}
            className={`${styles.input} ${errorClass} ${passwordClass}`}
            data-field-name={name}
            name={name}
            autoComplete={autoComplete}
            type={!pwdVisibility ? type : 'text'}
            value={value}
            ref={inputElement}
            placeholder={placeholder}
            disabled={disabled}
            onFocus={() => onInputFocusFunction()}
            onBlur={(e) => onInputBlurFunction(e)}
            onChange={(e) => onInputChange(e)}
            maxLength={maxLength}
          />
          {pwdHandler}
        </div>
        {showInputError && inputError}
        {!inputError && info && (
          <span className={styles.inputInfo}>{info}</span>
        )}
      </div>
    </div>
  );
}

InputField.propTypes = {
  id: PropTypes.string,
  theme: PropTypes.oneOf(['light', 'dark']),
  size: PropTypes.oneOf(['normal', 'small']),
  errorAlign: PropTypes.oneOf(['left', 'right']),
  className: PropTypes.string,
  type: PropTypes.string,
  dataHook: PropTypes.string,
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  subLabel: PropTypes.string,
  info: PropTypes.string,
  onPropertyChange: PropTypes.func,
  onInputBlur: PropTypes.func,
  onInputFocus: PropTypes.func,
  focusOnMount: PropTypes.bool,
  fullWidth: PropTypes.bool,
  value: PropTypes.any.isRequired,
  disabled: PropTypes.bool,
  showInputError: PropTypes.bool,
  error: PropTypes.string,
  inline: PropTypes.bool,
  placeholder: PropTypes.string,
  autoComplete: PropTypes.string,
  maxLength: PropTypes.string,
  isMobile: PropTypes.bool
};
